import {Injectable} from '@angular/core';

type ToastType = 'success' | 'error' | 'info' | 'warning' | 'primary' | 'secondary';

const DEFAULT_TOAST_DURATION = 3000;

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    constructor() {}

    async presentErrorToast(errorMessage: string, duration = DEFAULT_TOAST_DURATION) {
        this.presentCustomToast('error', errorMessage, duration);
    }

    async presentInfoToast(message: string, duration = DEFAULT_TOAST_DURATION) {
        this.presentCustomToast('info', message, duration);
    }

    async presentSuccessToast(message: string, duration = DEFAULT_TOAST_DURATION) {
        this.presentCustomToast('success', message, duration);
    }

    async presentWarningToast(message: string, duration = DEFAULT_TOAST_DURATION) {
        this.presentCustomToast('warning', message, duration);
    }

    private presentCustomToast(type: ToastType, message: string, duration: number) {
        const toastExisting = document.querySelector('flw-toast') as any;
        if (toastExisting && toastExisting.innerHTML === message) {
            return;
        }

        // Create the toast element
        const toast = document.createElement('flw-toast') as any;
        toast.type = type;
        toast.duration = duration;
        toast.innerHTML = message;

        const bar = this.getVisibleTopBar();
        toast.style.position = 'fixed';
        toast.style.zIndex = '9999';
        toast.style.width = 'auto';
        toast.style.transition = 'opacity 0.3s ease-in-out';

        if (bar) {
            const rect = bar.getBoundingClientRect();
            const inset = this.getFrameInset();
            toast.style.left = `${Math.max(rect.left, inset) + 16}px`;
            toast.style.right = `${Math.max(window.innerWidth - rect.right, inset) + 16}px`;
            toast.style.top = `calc(${rect.top}px + 20px + env(safe-area-inset-top, 0px))`;
        } else {
            const gutter = this.getFrameInset() + 30;
            toast.style.left = `${gutter}px`;
            toast.style.right = `${gutter}px`;
            toast.style.top = 'calc(20px + env(safe-area-inset-top, 0px))';
        }

        /**
         * flw-toast owns its own auto-dismiss timer (`duration`) and
         * emits `flwDismiss` both on user close and on timeout,
         */
        let dismissed = false;
        const dismissHandler = () => {
            if (dismissed) {
                return;
            }
            dismissed = true;
            toast.removeEventListener('flwDismiss', dismissHandler);
            toast.style.opacity = '0';
            setTimeout(() => {
                if (toast.parentNode) {
                    toast.parentNode.removeChild(toast);
                }
            }, 300);
        };

        toast.addEventListener('flwDismiss', dismissHandler);

        document.body.appendChild(toast);
    }

    private getFrameInset(): number {
        const raw = getComputedStyle(document.documentElement)
            .getPropertyValue('--app-frame-inset')
            .trim();
        const value = parseFloat(raw);
        if (!isNaN(value)) {
            return Math.max(0, value);
        }
        return Math.max(0, window.innerWidth / 2 - 644);
    }

    /**
     * Devuelve la cabecera visible más alta de la página actual para anclar el
     * toast justo debajo
     */
    private getVisibleTopBar(): Element | null {
        const selectors = [
            'flw-top-bar',
            'app-main-header',
            'app-generic-header',
            'app-header-desktop',
            'app-header',
            'ion-header',
        ];
        const candidates = document.querySelectorAll(selectors.join(','));
        let best: Element | null = null;
        let bestBottom = 0;

        candidates.forEach((el) => {
            const rect = el.getBoundingClientRect();
            const isVisible = rect.height > 0 && rect.width > 0;
            const isNearTop = rect.top < 200;
            if (isVisible && isNearTop && rect.bottom > bestBottom) {
                best = el;
                bestBottom = rect.bottom;
            }
        });

        return best;
    }
}
